import { useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import type { Reply, RepliesResponse } from '../sequences_api'
import { useMarkReplyRead } from '../sequences_api'
import { ReplyViewModal } from './ReplyViewModal'

/**
 * ==================== REPLIES TABLE ====================
 * Displays replies synced from EmailBison and stored locally.
 * Data is kept up-to-date via webhooks with manual sync available.
 */

interface RepliesTableProps {
  data: RepliesResponse | undefined
  isLoading: boolean
  bisonCampaignId: number
}

const columnHelper = createColumnHelper<Reply>()

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a')
  } catch {
    return dateStr
  }
}

function StatusBadge({
  label,
  active,
  colorClass,
}: {
  label: string
  active: boolean
  colorClass: string
}) {
  if (!active) return null
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
    >
      {label}
    </span>
  )
}

function RepliesTableContent({
  data,
  isLoading,
  bisonCampaignId,
}: RepliesTableProps) {
  const [selectedReply, setSelectedReply] = useState<Reply | null>(null)
  const markReplyRead = useMarkReplyRead(bisonCampaignId)

  const handleRowClick = (reply: Reply) => {
    setSelectedReply(reply)
    // Mark as read if not already read
    if (!reply.is_read) {
      markReplyRead.mutate({ replyId: reply.id, isRead: true })
    }
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('from_email_address', {
        header: 'From',
        size: 200,
        cell: (info) => {
          const name = info.row.original.from_name
          const email = info.getValue()
          return (
            <div>
              {name && (
                <p className="text-sm font-medium text-fg truncate">{name}</p>
              )}
              <p className="text-sm text-fg-secondary">{email}</p>
            </div>
          )
        },
      }),
      columnHelper.accessor('primary_to_email_address', {
        header: 'To',
        size: 180,
        cell: (info) => {
          const senderName = info.row.original.sender_email_name
          const email = info.getValue()
          return (
            <div>
              {senderName && (
                <p className="text-sm font-medium text-fg truncate">
                  {senderName}
                </p>
              )}
              <p className="text-sm text-fg-secondary">{email}</p>
            </div>
          )
        },
      }),
      columnHelper.accessor('subject', {
        header: 'Subject',
        size: 200,
        cell: (info) => (
          <p className="text-sm text-fg truncate" title={info.getValue() || ''}>
            {info.getValue() || '(no subject)'}
          </p>
        ),
      }),
      columnHelper.accessor('is_read', {
        header: 'Status',
        size: 180,
        cell: (info) => {
          const isRead = info.getValue()
          const isInterested = info.row.original.is_interested
          const isAutomatedReply = info.row.original.is_automated_reply
          return (
            <div className="flex flex-wrap gap-1">
              <StatusBadge
                label={isRead ? 'Read' : 'Unread'}
                active={true}
                colorClass={
                  isRead
                    ? 'bg-gray-100 text-gray-700'
                    : 'bg-blue-100 text-blue-800'
                }
              />
              <StatusBadge
                label="Interested"
                active={isInterested}
                colorClass="bg-green-100 text-green-800"
              />
              <StatusBadge
                label="Auto-reply"
                active={isAutomatedReply}
                colorClass="bg-yellow-100 text-yellow-800"
              />
            </div>
          )
        },
      }),
      columnHelper.accessor('date_received', {
        header: 'Date',
        size: 150,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('attachments', {
        header: 'Attachments',
        size: 100,
        cell: (info) => {
          const attachments = info.getValue()
          const count = attachments?.length || 0
          if (count === 0) {
            return <span className="text-sm text-fg-secondary">-</span>
          }
          return (
            <span className="inline-flex items-center text-sm text-fg-secondary">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              {count}
            </span>
          )
        },
      }),
    ],
    []
  )

  return (
    <>
      <DataTableContainer>
        <div className="[&_tbody_tr]:cursor-pointer">
          <DataTable
            tableKey="replies-table"
            data={data?.results ?? []}
            columns={columns}
            tableHeader={
              <div className="flex justify-between items-center w-full py-2">
                <p className="text-sm text-fg-secondary">
                  {data?.count ?? 0} repl
                  {(data?.count ?? 0) !== 1 ? 'ies' : 'y'}
                </p>
              </div>
            }
            isPaginationEnabled={false}
            loading={isLoading}
            onRowClick={(row) => handleRowClick(row.original)}
          />
        </div>
      </DataTableContainer>

      <ReplyViewModal
        reply={selectedReply}
        show={!!selectedReply}
        onClose={() => setSelectedReply(null)}
      />
    </>
  )
}

export function RepliesTable(props: RepliesTableProps) {
  return (
    <ColumnsStoreProvider tableKey="replies-table">
      <DataTableProvider tableKey="replies-table">
        <RepliesTableContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
